import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ChevronRight, Package, MapPin, Truck, CheckCircle2, Clock, Cookie } from "lucide-react";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const STATUS_META = {
  confirmed:  { label: "Confirmed",  icon: CheckCircle2, color: "text-blue-700 bg-blue-50 border-blue-200" },
  preparing:  { label: "Preparing",  icon: Cookie,       color: "text-amber-700 bg-amber-50 border-amber-200" },
  ready:      { label: "Ready",      icon: Package,      color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  completed:  { label: "Completed",  icon: CheckCircle2, color: "text-gray-600 bg-gray-100 border-gray-200" },
  cancelled:  { label: "Cancelled",  icon: Clock,        color: "text-red-700 bg-red-50 border-red-200" },
  pending_payment: { label: "Awaiting Payment", icon: Clock, color: "text-gray-500 bg-gray-50 border-gray-200" },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.confirmed;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[11px] font-bold uppercase tracking-wider ${meta.color}`} data-testid={`order-status-${status}`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

export default function MyOrdersPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    let stopped = false;
    const load = () => api.get("/orders/me")
      .then(r => { if (!stopped) setOrders((r.data || []).filter(o => o.payment_status === "paid")); })
      .catch(() => {})
      .finally(() => { if (!stopped) setLoading(false); });
    load();
    // Poll every 20s so status updates appear without manual refresh
    const t = setInterval(load, 20000);
    return () => { stopped = true; clearInterval(t); };
  }, [user]);

  if (!user) {
    return (
      <Layout>
        <section className="min-h-[60vh] flex items-center justify-center px-6">
          <div className="bg-white rounded-3xl shadow-lg p-8 max-w-md text-center">
            <h2 className="font-display font-extrabold text-2xl">Sign in to view your orders</h2>
            <Link to="/login" className="btn-pink mt-5 inline-block">LOG IN</Link>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="px-4 sm:px-6 py-10 sm:py-14 max-w-3xl mx-auto" data-testid="my-orders-page">
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-white">Your Orders</h1>
        <p className="text-white/90 mt-1 text-sm">Track every order from baking to delivery.</p>

        {loading ? (
          <div className="mt-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-white" /></div>
        ) : orders.length === 0 ? (
          <div className="mt-8 bg-white rounded-3xl p-8 text-center">
            <Cookie className="w-12 h-12 text-[#FF1F87] mx-auto mb-2" />
            <p className="text-[#4B5563]">You haven't ordered yet — your first box is just a tap away.</p>
            <Link to="/order" className="btn-pink mt-5 inline-block" data-testid="my-orders-empty-cta">ORDER COOKIES</Link>
          </div>
        ) : (
          <div className="mt-8 space-y-3">
            {orders.map(o => (
              <Link
                key={o.id}
                to={`/orders/${o.order_number}`}
                className="block bg-white rounded-2xl p-4 sm:p-5 hover:shadow-lg transition-all hover:-translate-y-0.5"
                data-testid={`order-card-${o.order_number}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-widest text-gray-500 font-bold">Order</p>
                    <p className="font-display font-extrabold text-lg truncate">#{o.order_number}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Placed {o.paid_at ? new Date(o.paid_at).toLocaleDateString() : new Date(o.created_at).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                      {o.method === "delivery" ? <Truck className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                      {o.method === "delivery" ? "Delivery" : "Pickup"}
                      {o.pickup_date ? ` · ${o.pickup_date}` : ""}
                    </p>
                  </div>
                  <div className="text-right flex flex-col items-end gap-2 flex-shrink-0">
                    <p className="font-display font-extrabold text-xl text-[#FF1F87]">${(o.total || 0).toFixed(2)}</p>
                    <StatusPill status={o.status} />
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300 mt-1 hidden sm:block" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}
